import { Check } from '@mui/icons-material';
import BoltIcon from '@mui/icons-material/Bolt';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Card, Container, Typography } from '@mui/material';

const FeatureCard = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <Card
    sx={{
      bgcolor: 'background.paper',
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
      borderRadius: 2,
      flex: 1,
    }}
  >
    <Box sx={{ color: 'primary.main', fontSize: 40 }}>{icon}</Box>
    <Typography variant='h6' sx={{ fontSize: 16, fontWeight: 500 }}>
      {title}
    </Typography>
    <Typography variant='body2' color='text.secondary' textAlign='center'>
      {description}
    </Typography>
  </Card>
);

export const AssessmentTrustPilot = ({
  onContinue,
}: {
  onContinue: () => void;
}) => {
  return (
    <Container maxWidth='md'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 6,
          gap: 4,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Check sx={{ fontSize: 40, color: 'common.white' }} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant='h3' component='h1' sx={{ mb: 1 }}>
            Thanks for your positive feedback!
          </Typography>
          <Typography variant='h5' color='text.secondary' sx={{ mt: 2 }}>
            Would you mind sharing your experience on Trustpilot?
          </Typography>
        </Box>

        <Card
          sx={{
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 4,
            width: '100%',
            maxWidth: 600,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'success.main',
                p: 1.5,
                px: 2,
                borderRadius: 2,
              }}
            >
              <StarIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Typography variant='h5' color='text.secondary'>
                Trustpilot
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                Help others discover great experiences
              </Typography>
            </Box>
          </Box>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            href='https://www.trustpilot.com/review/alignerr.com'
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            color='success'
            size='large'
            startIcon={<StarIcon sx={{ color: 'common.white' }} />}
            sx={{ px: 3, color: 'common.white' }}
          >
            Leave a review on Trustpilot
          </Button>

          <Button
            variant='outlined'
            size='large'
            onClick={onContinue}
            sx={{ px: 3 }}
          >
            Continue
          </Button>
        </Box>

        <Box sx={{ maxWidth: 800, mb: 4 }}>
          <Typography variant='h5' sx={{ mb: 1, fontWeight: 500 }}>
            <strong>Your reviews matter!</strong> They help us improve our
            services and help other candidates discover great opportunities. It
            only takes 2 minutes.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              mt: 3,
            }}
          >
            <FeatureCard
              icon={<BoltIcon fontSize='inherit' sx={{ color: '#FF9800' }} />}
              title='Quick & Easy'
              description='Takes less than 2 minutes'
            />
            <FeatureCard
              icon={
                <HandshakeIcon fontSize='inherit' sx={{ color: '#E91E63' }} />
              }
              title='Help Others'
              description='Share your experience'
            />
            <FeatureCard
              icon={
                <ShowChartIcon fontSize='inherit' sx={{ color: '#00BCD4' }} />
              }
              title='Improve Services'
              description='Help us get better'
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
